import {ReplaySubject} from 'rxjs';

import {DataSourceType} from '../../views/admin/application-settings/models/data-source-type.model';
import {IntegrationMode} from '../../views/admin/application-settings/models/integration-mode.model';
import {SchemaType} from '../../views/admin/application-settings/models/schema-type.model';
import {IntegrationTask} from '../../views/admin/application-settings/models/integration-task.model';
import {DataAttribute} from '../../views/admin/application-settings/models/data-attribute.model';
import {
    ACTIVE_INTEGRATION_STATES,
    IntegrationState
} from '../../views/admin/application-settings/models/integration-state.model';
import {SchoolProfileModel} from './entity-profile';
import {Application} from '../../views/admin/application-settings/models/application.model';
import {IntegrationTaskMap} from '../../views/admin/application-settings/models/integration-task-map.model';
import {DataSourceConfig} from '../../views/admin/application-settings/models/data-source-config.model';
import {
    Integration,
    INTEGRATION_TASKS_EDUTONE_TITLE_MAP,
    SsoAppAccessModes
} from '../../views/admin/application-settings/models/integration.model';
import {INTEGRATION_TASKS_ORDER_FOR_FILTER_RULES} from '@app/views/admin/application-settings/models/integration.model';
import {FiltersListService} from '@app/views/admin/application-settings/components/filters/filters-list-wrapper/filters-list.service';
import {MetadataModel} from '@app/core/model/filtering/metadata-model';
import {RestrictionSet} from '@app/core/model/filtering/restriction-set';
import {Argument} from '@app/core/model/filtering/argument';
import {PropertyFilterModel} from '@app/core/model/filtering/property-filter-model';
import {FilterPredicate} from '@app/core/model/filtering/filter-predicate';
import {EntityFilterModel, FilterState} from '@app/core/model/filtering/entity-filter-model';
import {FilterRuleMetadata} from '@app/core/model/filtering/filter-rule-metadata';
import {RulesAndTransforms} from '@app/core/model/filtering/rules-and-transforms';
import {FilterAction} from '@app/core/model/filtering/entity-filter-model';
import {FileItem} from 'ng2-file-upload/file-upload/file-item.class';

export enum IntegrationFormMode {
    INVITE = 'invite',
    INVITE_INDEPENDENT = 'invite-independent',
    OVERVIEW = 'overview',
    OVERVIEW_INDEPENDENT = 'overview-independent',
    OVERVIEW_PENDING_REQUEST = 'overview-pending-request',
    OVERVIEW_REQUEST = 'overview-request'
}
export class IntegrationFormOptions {
    visibleMessage = false;
    visibleSchools = true;
    editableSchools = true;
    visibleAttributes = true;
    editableAttributes = true;
    visibleFilters = true;
    editableFilters = true;
    visibleSsoFilters = true;
    editableSsoFilters = true;
    editableAttachment = true;
    visibleDataBrowsing = false;
    editableExpirationDate = false;
    visibleExpirationDate = false;
    visibleDataSourceConfig = false;
    editableAppAssignmentMode = false;
    visibleAppAssignmentMode = false;
    mode: IntegrationFormMode = IntegrationFormMode.INVITE;

    setAllReadOnly() {
        this.editableSchools = false;
        this.editableAttributes = false;
        this.editableFilters = false;
        this.editableSsoFilters = false;
        this.editableAttachment = false;
        this.editableAppAssignmentMode = false;
    }
}

export class SchoolSet {

    public oneSelectedSchoolGuid?: string;

    constructor(
        public schools: SchoolProfileModel[],
        public ic: IntegrationComplete
    ) {
        if (this.schools) {
            this.schools.forEach(school => {
                if (SchoolProfileModel.isActivated(school)) {
                    school.status = IntegrationState.SYNCED;
                }
            });
        }
    }

    displaySchools(mode: IntegrationFormMode, requestedAction: RequestedAction = null, currentRequest?: DataAccessRequestModel) {
        if (mode === IntegrationFormMode.INVITE) {

            if (this.oneSelectedSchoolGuid) {
                return this.schools.filter((s: SchoolProfileModel) => s.schoolGuid === this.oneSelectedSchoolGuid);
            } else {
                return this.schools;
            }

        } else if (mode === IntegrationFormMode.OVERVIEW) {
            if (this.oneSelectedSchoolGuid) {
                return this.schools.filter((s: SchoolProfileModel) => s.schoolGuid === this.oneSelectedSchoolGuid);
            } else {
                // We show all schools for Overview mode (the status is not taken into account)
                return this.schools;
                // return this.schools.filter((s: SchoolProfileModel) => [IntegrationState.SYNCED, IntegrationState.PENDING].indexOf(s.status)==-1);
            }
        } else if (mode === IntegrationFormMode.INVITE_INDEPENDENT) {
            return [];

        } else if (mode === IntegrationFormMode.OVERVIEW_INDEPENDENT) {
            return [];

        } else if (mode === IntegrationFormMode.OVERVIEW_PENDING_REQUEST) {

            // We show all schools for request
            const activatedSchools = this.schools.filter((s: SchoolProfileModel) => s.checked || !!s.modifiedBy);
            if (activatedSchools.length > 0) {
                return activatedSchools;
            } else {
                return this.schools;
            }

        } else if (mode === IntegrationFormMode.OVERVIEW_REQUEST) {
            // Show only changes for request overview
            return this.schools.filter((school: SchoolProfileModel) => {
                const schoolRoles: SchoolRole[] =
                    (currentRequest.activatedRolesForSchools && currentRequest.activatedRolesForSchools[school.schoolGuid])
                        ? currentRequest.activatedRolesForSchools[school.schoolGuid]
                        : [];
                return (!!school.modifiedBy
                        && (!requestedAction || (requestedAction === school.modifiedBy.requestedAction)))
                    || schoolRoles.some((role: SchoolRole) => role.requestedAction === requestedAction);
            });


        }
    }
}

export class IntegrationComplete {
    defaultIntegrationMetadata: Integration;
    currentIntegrationMetadata: Integration;
    application: Application;
    schoolSet: SchoolSet;
    filtersMetadata: RulesAndTransforms;
    filtersPredicatesMetadata: MetadataModel[];
    attrEntities: AttributeEntity[];
    _ruleSet: RestrictionSet;
    ruleSetChange: ReplaySubject<RestrictionSet> = new ReplaySubject();
    _ssoRuleSet: RestrictionSet;
    ssoRuleSetChange: ReplaySubject<RestrictionSet> = new ReplaySubject();
    sensitiveDataObj: {};

    get ruleSet(): RestrictionSet {
        return this._ruleSet;
    }

    set ruleSet(ruleSet: RestrictionSet) {
        this._ruleSet = ruleSet;
        this.ruleSetChange.next(this._ruleSet);
    }

    get ssoRuleSet(): RestrictionSet {
        return this._ssoRuleSet;
    }

    set ssoRuleSet(ssoRuleSet: RestrictionSet) {
        this._ssoRuleSet = ssoRuleSet;
        this.ssoRuleSetChange.next(this._ssoRuleSet);
    }

    isActiveStatus() {
        return ACTIVE_INTEGRATION_STATES.indexOf(this.currentIntegrationMetadata.state) > -1;
    }

    isExpiredStatus() {
        return [IntegrationState.EXPIRED, IntegrationState.EXPIRED_AND_SUSPENDED]
            .indexOf(this.currentIntegrationMetadata.state) > -1;
    }
}

export class AttributeMetadataModel {
    typeModel: SchemaType;
    propertyModel: DataAttribute[];
}

export class IntegrationDomain {

    static getInternal(integration: Integration): IntegrationDomain {
        return new IntegrationDomain(integration, integration.mode === IntegrationMode.EXPORT);
    }
    static getExternal(integration: Integration): IntegrationDomain {
        return new IntegrationDomain(integration, integration.mode !== IntegrationMode.EXPORT);
    }

    constructor(public integration: Integration,
                private sourceIsInternal: boolean) {
    }
    get dataSourceType() {
        return this.sourceIsInternal ?
            this.integration.source.dataSourceType :
            this.integration.target.dataSourceType;
    }
    getTaskSourceType(task: IntegrationTask) {
        if (this.sourceIsInternal) {
            return task.sourceType;
        } else {
            return task.targetType;
        }
    }
    displayTypeName(task: (IntegrationTask | RequestedTaskModel)) {
        if (this.sourceIsInternal) {
            if (this.integration.source.dataSourceType === DataSourceType.SFTP) {
                return task.csvFileName + '.csv';
            } else {
                return task.sourceType;
            }
        } else {
            if (this.integration.target.dataSourceType === DataSourceType.SFTP) {
                return task.csvFileName + '.csv';
            } else {
                return task.targetType;
            }
        }
    }
    equalsEntityToTask(a: AttributeEntity, b: (IntegrationTask | RequestedTaskModel)): boolean {
        return a.sourceType === b.sourceType && a.targetType === b.targetType;
    }
    belongsToEntity(a: SchemaType, b: (IntegrationTask | RequestedTaskModel)): boolean {
        if (this.sourceIsInternal) {
            return a.denom === b.sourceType;
        } else {
            return a.denom === b.targetType;
        }
    }
    /*    belongsToEntityReq(a: SchemaType, b: RequestedTaskModel): boolean {
            if (this.sourceIsInternal) {
                return a.denom === b.sourceType;
            } else {
                return a.denom === b.targetType;
            }
        }*/

    /* belongAttrToEntity(a: DataAttribute, b: IntegrationTask): boolean {
        if (this.sourceIsInternal) {
            return a.entityTypeDenom === b.sourceType;
        } else {
            return a.entityTypeDenom === b.targetType;
        }
    }*/
    equalsAttrsMap(a: DataAttribute, b: IntegrationTaskMap): boolean {
        if (this.sourceIsInternal) {
            return a.propertyName === b.sourcePropertyName;
        } else {
            return a.propertyName === b.targetPropertyName;
        }
    }

    equalsAttrsReq(a: RequestedAttributeModel, b: AttributeEntityItem): boolean {
        if (this.sourceIsInternal) {
            return a.sourcePropertyName === b.sourcePropertyName;
        } else {
            return a.targetPropertyName === b.targetPropertyName;
        }
    }

    /* equalsTasks(a: (IntegrationTask | RequestedTaskModel), b: (IntegrationTask | RequestedTaskModel)): boolean {
        return a.sourceType === b.sourceType && a.targetType === b.targetType;
    }*/

    get dataSource(): DataSourceConfig {
        return this.sourceIsInternal ? this.integration.source : this.integration.target;
    }

    getTaskAttributes(task: IntegrationTask): DataAttribute[] {
        if (this.sourceIsInternal) {
            return task.sourceAttributeList;
        } else {
            return task.targetAttributeList;
        }
    }

    getMappingAttrPropertyName(attr: IntegrationTaskMap): string {
        if (this.sourceIsInternal) {
            return attr.sourcePropertyName;
        } else {
            return attr.targetPropertyName;
        }
    }

    setMappingProperty(mappingItem: IntegrationTaskMap, attribute: DataAttribute) {
        if (this.sourceIsInternal) {
            mappingItem.sourceProperty = attribute;
        } else {
            mappingItem.targetProperty = attribute;
        }
    }

    getMappingProperty(mappingItem: IntegrationTaskMap): DataAttribute {
        if (this.sourceIsInternal) {
            return mappingItem.sourceProperty;
        } else {
            return mappingItem.targetProperty;
        }
    }
}

export enum RequestedAction {
    ADD = 'ADD',
    UPDATE = 'UPDATE',
    REMOVE = 'REMOVE',
    PII_SHIELD_UPDATE = 'PII_SHIELD_UPDATE'
}
export enum DataAccessRequestStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED'
}

export enum DataAccessConsentsStatus {
    PENDING = 'PENDING APPROVAL',
    PENDING_CURRENT_USER = 'AWAITING YOUR REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    REQUESTED = 'REQUESTED'
}

export interface CanAction {
    uniqueId: string;
    requestedAction: RequestedAction;
    getId(): any;
}
export class OrgIdentity {
    constructor(
        public identifier: string,
        public name: string) {
    }
}
export function genId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}
export class RequestedOrganizationModel implements CanAction {
    uniqueId: string;
    requestedAction: RequestedAction;
    customOrgName: string;
    source: OrgIdentity;
    edutone: OrgIdentity;
    target: OrgIdentity;
    activatedRolesForSchool: SchoolRole[];
    customOrgNameAndActivatedRolesForSchoolPrettyView: string;
    getId() {
        return this.edutone.identifier;
    }
    constructor() {
        this.uniqueId = genId();
    }
}
export class RequestedAttributeModel implements CanAction {
    uniqueId: string;
    requestedAction: RequestedAction;
    mappingId: string;
    sourcePropertyName: string;
    targetPropertyName: string;
    backToSourcePropertyName: string;
    friendlyName: string;
    piiShieldAction: PIIShieldAction;

    static isEqual(a: RequestedAttributeModel, b: RequestedAttributeModel): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    constructor() {
        this.uniqueId = genId();
    }

    getId() {
        return this.mappingId;
    }

    equals(attr: RequestedAttributeModel): boolean {
        return RequestedAttributeModel.isEqual(this, attr);
    }
}

export class RequestedTaskModel implements CanAction {
    uniqueId: string;
    requestedAction: RequestedAction;
    taskId: string;
    sourceType: string;
    targetType: string;
    displayType: string;
    integration: any;
    csvFileName: string;
    requestedAttributes: RequestedAttributeModel[];

    static isEqual(a: RequestedTaskModel, b: RequestedTaskModel): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    constructor() {
        this.uniqueId = genId();
    }

    getId() {
        return this.uniqueId;
    }

    equals(task: RequestedTaskModel): boolean {
        return RequestedTaskModel.isEqual(this, task);
    }
}
export class RequestedArgumentModel extends Argument implements CanAction {
    uniqueId: string;
    requestedAction: RequestedAction;
    isDate: boolean;
    isDateBetween: boolean;
    schoolName: string;
    localId: string;

    static getSchoolObjFromSchoolNameAndLocalId(argumentName: string): SchoolProfileModel {
        const school = new SchoolProfileModel();
        const lastDashIndex = argumentName.lastIndexOf(' - ');
        if (lastDashIndex > -1) {
            school.schoolName = argumentName.substring(0, lastDashIndex);
            school.localId = argumentName.substring(lastDashIndex + 3);

            return school;
        }

        school.schoolName = argumentName;
        return school;
    }

    static setSchoolNameAndLocalIdFromArgumentName(argument: RequestedArgumentModel): RequestedArgumentModel | null {
        const lastDashIndex = argument.name.lastIndexOf(' - ');
        if (lastDashIndex > -1) {
            argument.schoolName = argument.name.substring(0, lastDashIndex);
            argument.localId = argument.name.substring(lastDashIndex + 3);

            return argument;
        }

        argument.schoolName = argument.name;
        return argument;
    }

    getId() {
        return this.id + this.name;
    }

    constructor(
        id: string = null,
        name: string = null,
        requestedAction: RequestedAction = RequestedAction.ADD) {

        super(id, name);
        this.requestedAction = requestedAction;
        this.uniqueId = genId();
    }
}

export class ItemOfSchoolsArgumentListModel {
    ids: string[];
    names: string[];
    schoolNames: string[];
    localIds: string[];
    schoolNamesString: string;
    idsString: string;
    filtersList: RequestedFilterViewModel[];

    constructor(
    ) {}
}

export class RequestedPropertyModel extends PropertyFilterModel implements CanAction {
    uniqueId: string;
    arguments: RequestedArgumentModel[];
    requestedAction: RequestedAction;
    propertyFriendlyName: string;

    static isEqual(a: RequestedPropertyModel, b: RequestedPropertyModel): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    constructor(
        propertyName: string = null,
        predicateName: FilterPredicate = null,
        args: Array<RequestedArgumentModel> = <RequestedArgumentModel[]>[],
        propertyFilterId: string = null,
        requestedAction: RequestedAction = RequestedAction.ADD) {

        super(propertyName, predicateName, args, propertyFilterId);
        this.arguments = args;
        this.requestedAction = requestedAction;
        this.uniqueId = genId();
    }

    getId() {
        return this.propertyFilterId;
    }

    equals(property: RequestedPropertyModel): boolean {
        return RequestedPropertyModel.isEqual(this, property);
    }
}

export class RequestedFilterModel extends EntityFilterModel implements CanAction {
    uniqueId: string;
    taskIdentifier: string;
    eduToneEntityType: string;
    schoolGuid: string;
    propertyFilters: Array<RequestedPropertyModel> = <RequestedPropertyModel[]>[];
    requestedAction: RequestedAction;

    static isEqual(a: RequestedFilterModel, b: RequestedFilterModel): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    static setRequestedAction(filter: RequestedFilterModel, requestedAction: RequestedAction = RequestedAction.ADD): RequestedFilterModel {
        filter.requestedAction = requestedAction;

        filter.propertyFilters = filter.propertyFilters.map((propertFilter: RequestedPropertyModel) => {
            propertFilter.requestedAction = requestedAction;

            propertFilter.arguments = propertFilter.arguments.map((argument: RequestedArgumentModel) => {
                argument.requestedAction = requestedAction;
                return argument;
            });

            return propertFilter;
        });

        return filter;
    }

    constructor(taskId: string = null,
        action: FilterAction = null,
        propertyFilters: Array<RequestedPropertyModel> = null,
        state: FilterState = FilterState.ACTIVE,
        entityFilterId: string = null,
        linkedEntityTypes: Array<string> = <string[]>[],
        isMyFilter: boolean = true,
        requestedAction: RequestedAction = RequestedAction.ADD
    ) {
        super(taskId, action, propertyFilters, state, entityFilterId, linkedEntityTypes, isMyFilter);
        this.taskIdentifier = taskId;
        this.requestedAction = requestedAction;
        this.uniqueId = genId();
    }

    getId() {
        return this.entityFilterId;
    }

    equals(filter: RequestedFilterModel): boolean {
        return RequestedFilterModel.isEqual(this, filter);
    }
}

export class RequestedFilterViewModel extends RequestedFilterModel {
    metadata: FilterRuleMetadata = null;
    editMode = false;
    firstSchool: SchoolProfileModel;
    trackByUniqueId: string;
    selectedEntity: string;

    static copyFilter(filter: RequestedFilterViewModel): RequestedFilterViewModel {
        const newFilter = <RequestedFilterViewModel>copyData({...filter, metadata: null});
        newFilter.metadata = filter.metadata;
        newFilter.entityFilterId = null;
        newFilter.isMyFilter = true;

        newFilter.propertyFilters = newFilter.propertyFilters.map((propertyFilter: RequestedPropertyModel) => {
            propertyFilter.arguments = propertyFilter.arguments.filter((arg: RequestedArgumentModel) =>
                !arg.requestedAction || arg.requestedAction !== RequestedAction.REMOVE
            );
            return (<RequestedPropertyModel>{...propertyFilter, propertyFilterId: null});
        });

        return <RequestedFilterViewModel>RequestedFilterModel.setRequestedAction(newFilter, RequestedAction.ADD);
    }

    static copyFilterObj(filter: RequestedFilterViewModel): RequestedFilterViewModel {
        const newFilter = <RequestedFilterViewModel>copyData({...filter, metadata: null});
        newFilter.metadata = filter.metadata;
        return newFilter;
    }

    static sortFiltrations(filtrations: RequestedFilterViewModel[]): void {
        filtrations.sort((filterA: RequestedFilterViewModel, filterB: RequestedFilterViewModel) => {
            if (filterA.isMyFilter < filterB.isMyFilter) {
                return -1;
            }
            if (filterA.isMyFilter > filterB.isMyFilter) {
                return 1;
            }

            const sortResultBasedOnIntegrationTasksOrderForFilterRules =
                (INTEGRATION_TASKS_ORDER_FOR_FILTER_RULES[filterA.eduToneEntityType] || 100)
                - (INTEGRATION_TASKS_ORDER_FOR_FILTER_RULES[filterB.eduToneEntityType] || 100);

            if (filterA.firstSchool || filterB.firstSchool) {
                if (filterA.firstSchool && filterB.firstSchool) {
                    if (sortResultBasedOnIntegrationTasksOrderForFilterRules === 0) {

                        const result = filterA.firstSchool.schoolName.localeCompare(
                            filterB.firstSchool.schoolName, 'en', {sensitivity: 'base'});

                        if (filterA.firstSchool.schoolName && filterB.firstSchool.schoolName) {

                            if (result) {
                                return result;
                            }
                        } else {
                            return -result;
                        }
                    } else {
                        return sortResultBasedOnIntegrationTasksOrderForFilterRules;
                    }

                } else {
                    return filterA.firstSchool && filterA.firstSchool.schoolName ? -1 : 1;
                }
            }

            return sortResultBasedOnIntegrationTasksOrderForFilterRules;
        });
    }

    static processFilterWithSchoolId(filter: RequestedFilterViewModel): void {
        filter.propertyFilters.forEach((propertFilter: RequestedPropertyModel) => {
            const isPropertyFilterWithSchoolId =
                FiltersListService.checkIsSchoolIdSelected(propertFilter.propertyName)
                && propertFilter.arguments.length > 0
                && (propertFilter.predicateName === FilterPredicate.EQUAL
                    || propertFilter.predicateName === FilterPredicate.NOT_EQUAL
                    || propertFilter.predicateName === FilterPredicate.DOES_NOT_CONTAIN
                    || propertFilter.predicateName === FilterPredicate.CONTAINS);

            if (isPropertyFilterWithSchoolId) {
                // set firstSchool
                if (!filter.firstSchool) {

                    const firstArgumentWithSchool = propertFilter.arguments.find((argument: RequestedArgumentModel) =>
                        !!RequestedArgumentModel.getSchoolObjFromSchoolNameAndLocalId(argument.name));
                    const school = RequestedArgumentModel.getSchoolObjFromSchoolNameAndLocalId(firstArgumentWithSchool.name);

                    if (school) {
                        filter.firstSchool = school;
                    }
                }

                // transform schoolId arguments "schoolName - localId" => argument.schoolName, argument.localId
                propertFilter.arguments = propertFilter.arguments.map((argument: RequestedArgumentModel) => {
                    const newArgument = RequestedArgumentModel.setSchoolNameAndLocalIdFromArgumentName(argument);
                    return newArgument;
                });
            }
        });
    }

    static processFiltration(filtrationsToStore: RequestedFilterViewModel[],
        filtrationsFromResponse: RequestedFilterViewModel[],
        task: IntegrationTask,
        edutoneDS: string
    ): RequestedFilterViewModel[] {
        if (filtrationsFromResponse && (filtrationsFromResponse.length > 0) ) {
            filtrationsFromResponse = filtrationsFromResponse
                .map((filter: RequestedFilterViewModel) => {
                    filter.eduToneEntityType = INTEGRATION_TASKS_EDUTONE_TITLE_MAP[task[`${edutoneDS}Type`]]
                        ? INTEGRATION_TASKS_EDUTONE_TITLE_MAP[task[`${edutoneDS}Type`]]
                        : task[`${edutoneDS}Type`];

                    RequestedFilterViewModel.processFilterWithSchoolId(filter);

                    return filter;
                });
            return filtrationsToStore.concat(filtrationsFromResponse);
        }
        return filtrationsToStore;
    }
}

export class IntegrationRuleModel {
    filtrations: RequestedFilterViewModel[];
    sso: RequestedFilterViewModel[];
    transformations: EntityFilterModel[];
    streamTransformations: EntityFilterModel[];
    validations: EntityFilterModel[];
}

export class IntegrationRulesMap {
    [key: string]: IntegrationRuleModel;
}

export class StagedPropertyModel extends RequestedPropertyModel {
    arguments: RequestedArgumentModel[];
    propertyFriendlyName: string;
    newPropertyName?: string;
    newPredicateName?: FilterPredicate;
}

export class StagedFilterModel extends RequestedFilterViewModel {
    propertyFilters: Array<StagedPropertyModel> = <StagedPropertyModel[]>[];
    removedOldFilterLinkedEntityTypesObj?: {}; // { oldLinkedEntityType : oldLinkedEntityType }
    addedFilterLinkedEntityTypesObj?: {}; // { newLinkedEntityType : newLinkedEntityType }
}

export class DataAccessRequestModel {

    guid: string;
    integrationId: string;
    status: DataAccessRequestStatus;

    senderOrgGuid: string;
    vendor: string;
    requestMessage: string;
    requestTs: Date;

    recipientOrgGuid: string;
    provider: string;
    replyMessage: string;
    replyTs: Date;
    expirationTs: Date;
    appAssignmentMode: SsoAppAccessModes;

    requestedOrganizations: RequestedOrganizationModel[] = [];
    requestedTasks: RequestedTaskModel[] = [];
    requestedFilters: RequestedFilterModel[] = [];
    ssoRequestedFilters: RequestedFilterModel[] = [];

    applicationName?: string;
    applicationGuid?: string;

    consentStatus?: DataAccessConsentsStatus;
    vendorConsentStatus?: DataAccessConsentsStatus;
    institutionConsentStatus?: DataAccessConsentsStatus;

    institutionType: string;
    institutionState: string;

    files: DataAccessRequestFileLightModel[];
    activatedRolesForSchools: RequestedSchoolRoles;

    isTrial: boolean;
}

export class DataAccessRequestFileLightModel {
    guid: string;
    requestId: string;
    fileName: string;
    orgGuid: string;
    size: number;

    constructor(
        public file: FileItem = null
    ) {
    }
}

export class SchoolRole {
    constructor(
        public sourceType: string,
        public targetType: string,
        public requestedAction?: RequestedAction,
        public school?: SchoolProfileModel
    ) {}
}

export class RequestedSchoolRoles  {
    [schoolGuid: string]: any | SchoolRole[];
    requestedAction: RequestedAction;
}


export class AttributeEntityItem extends DataAttribute {
    mappingId: string;
    sourcePropertyName: string;
    targetPropertyName: string;
    backToSourcePropertyName: string;
    taskId: string;
    enabled: boolean;
    skipIfMissing: boolean;
    modifiedBy: RequestedAttributeModel;
    attrEntity: AttributeEntity;
    sameMappedAttributes: AttributeEntityItem[];
    piiShieldAction: PIIShieldAction;
    initialPIIShieldAction: PIIShieldAction;

    static isEqual(a: AttributeEntityItem, b: AttributeEntityItem): boolean {
        // Don't compare sameMappedAttributes
        return a && b && (
            (a.mappingId === b.mappingId)
            && (a.taskId === b.taskId)
            && (a.sourcePropertyName === b.sourcePropertyName)
            && (a.targetPropertyName === b.targetPropertyName)
            && (a.backToSourcePropertyName === b.backToSourcePropertyName)
            && (a.enabled === b.enabled)
            && (a.skipIfMissing === b.skipIfMissing)
            && (!!a.attrEntity === !!b.attrEntity)
            && (!a.attrEntity || AttributeEntity.isEqual(a.attrEntity, b.attrEntity))
            && (!!a.modifiedBy === !!b.modifiedBy)
            && (!a.modifiedBy || RequestedAttributeModel.isEqual(a.modifiedBy, b.modifiedBy))
        );
    }

    static instanceFromRequestedTaskItem(item: RequestedAttributeModel, attrDesc: DataAttribute): AttributeEntityItem {
        const attr = new AttributeEntityItem();
        attr.backToSourcePropertyName = item.backToSourcePropertyName;
        attr.mappingId = item.mappingId;
        attr.sourcePropertyName = item.sourcePropertyName;
        attr.targetPropertyName = item.targetPropertyName;

        attr.domain = attrDesc.domain;
        attr.propertyName = attrDesc.propertyName;
        attr.friendlyName = attrDesc.friendlyName;
        attr.entityTypeDenom = attrDesc.entityTypeDenom;
        attr.propertyTypeDenom = attrDesc.propertyTypeDenom;
        attr.required = attrDesc.required;
        attr.ownerRef = attrDesc.ownerRef;
        attr.order = attrDesc.ownerRef;

        return attr;
    }

    equals(attr: AttributeEntityItem): boolean {
        return AttributeEntityItem.isEqual(this, attr);
    }
}

export enum PIIShieldAction {
    IS_SUPPORT = 'IS_SUPPORT',
    TRANSFORM = 'TRANSFORM'
}

export class AttributeEntity {
    uniqueId: string;
    taskId: string;
    sourceType: string;
    targetType: string;
    edutoneEntity: string;
    displayType: string;
    displayExternalType: string;
    csvFileName: string;
    attributes: AttributeEntityItem[];
    modifiedBy: RequestedTaskModel;
    position = 1000;
    sameEntityList: AttributeEntity[] = [];

    static isEqual(a: AttributeEntity, b: AttributeEntity): boolean {
        // Don't compare attributes & sameEntityList
        return a && b && (
            (a.uniqueId === b.uniqueId)
            && (a.taskId === b.taskId)
            && (a.sourceType === b.sourceType)
            && (a.targetType === b.targetType)
            && (a.edutoneEntity === b.edutoneEntity)
            && (a.displayType === b.displayType)
            && (a.displayExternalType === b.displayExternalType)
            && (a.csvFileName === b.csvFileName)
            && (a.position === b.position)
            && (!!a.modifiedBy === !!b.modifiedBy)
            && (!a.modifiedBy || RequestedTaskModel.isEqual(a.modifiedBy, b.modifiedBy))
        );
    }

    static instanceFromRequestedTask(task: RequestedTaskModel, entityDesc: SchemaType): AttributeEntity {
        const ent = new AttributeEntity();
        ent.uniqueId = genId();
        ent.sourceType = task.sourceType;
        ent.displayType = task.displayType;
        ent.targetType = task.targetType;
        ent.modifiedBy = task;
        ent.attributes = [];
        ent.csvFileName = task.csvFileName;
        ent.taskId = task.taskId;
        ent.sameEntityList.push(ent);
        return ent;
    }

    static instanceFromIntegrationTask(task: IntegrationTask): AttributeEntity {
        const ent = new AttributeEntity();
        ent.uniqueId = genId();
        ent.taskId = task.taskId;
        ent.sourceType = task.sourceType;
        ent.targetType = task.targetType;
        ent.csvFileName = task.csvFileName;
        ent.attributes = [];
        ent.sameEntityList.push(ent);
        return ent;
    }

    equals(entity: AttributeEntity): boolean {
        return AttributeEntity.isEqual(this, entity);
    }
}
export class OneAttributeEntity {
    static isEqual(a: OneAttributeEntity, b: OneAttributeEntity): boolean {
        return a && b && (
            (a.integrationId === b.integrationId)
            && (a.taskId === b.taskId)
            && (a.sourceType === b.sourceType)
            && (a.displayType === b.displayType)
            && (a.edutoneEntity === b.edutoneEntity)
            && (!!a.attribute === !!b.attribute)
            && (!a.attribute || AttributeEntityItem.isEqual(a.attribute, b.attribute))
        );
    }

    constructor(
        public integrationId: string,
        public taskId: string,
        public sourceType: string,
        public displayType: string,
        public edutoneEntity: string,
        public attribute: AttributeEntityItem
    ) {
    }

    equals(attr: OneAttributeEntity): boolean {
        return OneAttributeEntity.isEqual(this, attr);
    }
}

export enum IntegrationChangeTypes {
    SCHOOL_LIST = 'ch-school',
    ATTR_CHANGED = 'attr-changed',
    PII_SHIELD_UPDATE = 'pii-shield-update',
    SCHOOL_ROLE = 'school-role'
}
export class IntegrationChangeEvent {
    type: string;
    action: string;
    object: any;
}

export const copyData = (source: any): any => source ? JSON.parse(JSON.stringify(source)) : null;

export const onlyUnique = (value: any, index: number, self: any[]): boolean => self.indexOf(value) === index;

export const areObjectsEqual = (source: any, target: any): boolean => !!(source && target && JSON.stringify(source) === JSON.stringify(target));

export const areMapObjectsEqual = (mapSource: any, mapTarget: any): boolean => {
    const mapSourceValuesList = [];
    const mapTargetValuesList = [];
    if (mapSource) {
        mapSource.forEach((value: any, key: string) => {
            mapSourceValuesList.push({key: key, value: value});
        });
    }
    if (mapTarget) {
        mapTarget.forEach((value: any, key: string) => {
            mapTargetValuesList.push({key: key, value: value});
        });
    }
    return JSON.stringify(mapSourceValuesList) === JSON.stringify(mapTargetValuesList);
};

export const asyncFunction = (source: any, callback: (arg?: any) => any, propertyName: string, delay: number = 0): any => {
    if (!source[propertyName]) {
        setTimeout(() => {
            if (source[propertyName]) {
                callback();
            } else {
                asyncFunction(source, callback, propertyName);
            }
        }, delay);
    } else {
        callback();
    }
};

export class IntegrationWithRequestModel {
    public integrationModel: Integration;
    public requestModel: DataAccessRequestModel;
}

export const ACTIVATED_ROLES_FOR_SCHOOLS_RESTRICTIONS = new Map<string, ActivatedRolesForSchoolsRestrictionOption>([
    ['sp engagement', {applicationName: 'sp engagement', allowedSSORoles: ['SchoolAdmin']}],
]);

export interface ActivatedRolesForSchoolsRestrictionOption {
    applicationName: string;
    allowedSSORoles: string[];
}
